import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Tooltip } from 'antd';
import { CheckOutlined, FontSizeOutlined } from '@ant-design/icons';
import useHankoAuth from '../../hooks/useHankoAuth';
import {NavLink} from "react-router-dom";
import NotificationBell from "../notifications/NotificationBell";

const TopNavIcons = ({ isMobile = false }) => {
    const { t, i18n } = useTranslation();

    const { user, logout } = useHankoAuth();

    const [fontSize, setFontSize] = useState(() => {
        // Load font size from localStorage, default to 'medium'
        return localStorage.getItem('appFontSize') || 'medium';
    });

    // Apply font size to document root when it changes
    useEffect(() => {
        const fontSizeMap = {
            small: '14px',
            medium: '16px',
            large: '18px',
            extraLarge: '20px'
        };

        document.documentElement.style.fontSize = fontSizeMap[fontSize];
        localStorage.setItem('appFontSize', fontSize);
    }, [fontSize]);

    const iconStyle = {
        marginLeft: isMobile ? '0' : '1rem',
        cursor: 'pointer',
        fontSize: '1.2rem',
        color: isMobile ? '#FFFFFF' : '#27343A',
    };

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    const activeLang = (i18n.language || '').split('-')[0];
    const languageMenu = {
        items: [
            { key: 'en', label: t('topNavIcons.english'), icon: activeLang === 'en' ? <CheckOutlined /> : null },
            { key: 'fi', label: t('topNavIcons.finnish'), icon: activeLang === 'fi' ? <CheckOutlined /> : null },
            { key: 'sv', label: t('topNavIcons.swedish'), icon: activeLang === 'sv' ? <CheckOutlined /> : null },
        ],
        selectedKeys: [activeLang],
        selectable: true,
        onClick: ({ key }) => {
            if (key === 'en' || key === 'fi' || key === 'sv') changeLanguage(key);
        }
    };

    const fontSizeMenu = {
        items: [
            { key: 'small', label: t('topNavIcons.small', 'Small'), icon: fontSize === 'small' ? <CheckOutlined /> : null },
            { key: 'medium', label: t('topNavIcons.medium', 'Medium'), icon: fontSize === 'medium' ? <CheckOutlined /> : null },
            { key: 'large', label: t('topNavIcons.large', 'Large'), icon: fontSize === 'large' ? <CheckOutlined /> : null },
            { key: 'extraLarge', label: t('topNavIcons.extraLarge', 'Extra Large'), icon: fontSize === 'extraLarge' ? <CheckOutlined /> : null },
        ],
        selectedKeys: [fontSize],
        selectable: true,
        onClick: ({ key }) => {
            if (['small', 'medium', 'large', 'extraLarge'].includes(key)) {
                setFontSize(key);
            }
        }
    };

    const userMenu = {
        items: [
            { key: 'email', label: (
                <NavLink to='/settings/account' style={{ textDecoration: 'none', color: 'inherit' }}>
                    {user?.email || 'User'}
                </NavLink>
            ) },
            { type: 'divider' },
            { key: 'logout', label: t('topNavIcons.logout') },
        ],
        onClick: ({ key }) => {
            if (key === 'logout') logout();
        }
    };

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '1.25rem' : '0', marginLeft: isMobile ? '0' : 'auto' }}>
            <NotificationBell iconStyle={iconStyle} />
            <a
                href="/ohjeet"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('topNavIcons.help', 'Ohjeet')}
                style={{ textDecoration: 'none' }}
            >
                <Tooltip title={t('topNavIcons.help')}>
                    <i className="fas fa-question-circle" style={iconStyle}></i>
                </Tooltip>
            </a>
            <NavLink to="/settings" style={{ textDecoration: 'none' }}>
                <Tooltip  title={t('topNavIcons.settings')}>
                    <i className="fas fa-cog" style={iconStyle}></i>
                </Tooltip>
            </NavLink>

            {/* Font Size Dropdown */}
            <Dropdown menu={fontSizeMenu} trigger={["click"]} placement="bottom">
                <span>
                    <Tooltip title={t('topNavIcons.fontSize', 'Font Size')}>
                        <FontSizeOutlined style={iconStyle} />
                    </Tooltip>
                </span>
            </Dropdown>

            {/* Language Dropdown */}
            <Dropdown menu={languageMenu} trigger={["click"]} placement="bottom">
                <span>
                    <Tooltip title={t('topNavIcons.language')}>
                        <i className="fas fa-globe" style={iconStyle}></i>
                    </Tooltip>
                </span>
            </Dropdown>

            {/* User Dropdown with Logout */}
            <Dropdown menu={userMenu} trigger={["click"]} placement="bottom">
                <span>
                    <Tooltip title={t('topNavIcons.user')}>
                        <i className="fas fa-user" style={iconStyle}></i>
                    </Tooltip>
                </span>
            </Dropdown>
        </div>
    );
};

export default TopNavIcons;
